import { useNavigate } from 'react-router-dom';
import { Eye, ArrowLeft, Lock, Pencil, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

/**
 * Bandeau affiché quand la personne a ouvert, avec le sélecteur de société, une société autre que la sienne
 * (lot 1, étape 1.7 ; avant : « Vue PDG », CTO-ACCESS-001).
 *
 * Deux états visuels :
 *  - Lecture seule (défaut) : bandeau orange, toggle "🔒 Lecture seule".
 *  - Écriture activée       : bandeau rouge + warning "⚠ Modifications
 *    activées" pour rappeler la responsabilité au PDG.
 *
 * Le toggle envoie `X-PDG-View-Write: true` sur toutes les requêtes API
 * suivantes → backend autorise POST/PATCH/PUT/DELETE avec trace d'audit.
 */
export const PDGViewBanner = () => {
  const navigate = useNavigate();
  const { pdgViewFiliale, pdgViewFilialeName, pdgViewWrite, exitPDGView, setPdgViewWrite } = useAuthStore();

  if (!pdgViewFiliale) return null;

  const handleExit = async () => {
    await exitPDGView();
    navigate('/');
  };

  // TICKET-004 C-05 : fenêtre de l'application (la boîte native gelait l'onglet sous un automate).
  const toggleWrite = async () => {
    const next = !pdgViewWrite;
    if (next) {
      const ok = await demander.confirmer({
        titre: `Activer le mode Écriture sur « ${pdgViewFilialeName ?? pdgViewFiliale} » ?`,
        message: 'Vos modifications seront appliquées directement sur cette société et tracées (email, action, adresse). '
          + 'Le mode reste actif dans cet onglet jusqu’à sa fermeture, ou jusqu’au changement de société.',
        confirmer: 'Activer l’écriture',
        danger: true,
      });
      if (!ok) return;
    }
    setPdgViewWrite(next);
  };

  const writeMode = pdgViewWrite;

  return (
    <div
      role="alert"
      style={{
        position:        'sticky',
        top:             0,
        zIndex:          100,
        background:      writeMode
          ? 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)'
          : 'linear-gradient(90deg, #f97316 0%, #ea580c 100%)',
        color:           'white',
        padding:         '10px 16px',
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'space-between',
        gap:             8,
        boxShadow:       writeMode
          ? '0 2px 12px rgba(220,38,38,0.45)'
          : '0 2px 8px rgba(234,88,12,0.3)',
        fontSize:        13,
        fontWeight:      600,
        flexWrap:        'wrap',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
        {writeMode ? <AlertTriangle size={16} /> : <Eye size={16} />}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {pdgViewFilialeName ?? pdgViewFiliale}
          <span style={{ marginLeft: 8, opacity: 0.9, fontWeight: 500 }}>
            {writeMode ? '· ⚠ Modifications activées' : '· Lecture seule'}
          </span>
        </span>
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Toggle Lecture seule / Écriture */}
        <button
          type="button"
          onClick={toggleWrite}
          title={writeMode
            ? 'Repasser en lecture seule (bloque les modifications)'
            : 'Activer l\'écriture — attention, tu modifies directement les données de cette filiale'}
          style={{
            background:   writeMode ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.18)',
            color:        'white',
            border:       '1px solid rgba(255,255,255,0.4)',
            borderRadius: 6,
            padding:      '5px 12px',
            fontSize:     12,
            fontWeight:   700,
            cursor:       'pointer',
            display:      'inline-flex',
            alignItems:   'center',
            gap:          5,
          }}
        >
          {writeMode ? (<><Pencil size={13} /> Écriture ON</>) : (<><Lock size={13} /> Lecture seule</>)}
        </button>

        <button
          type="button"
          onClick={handleExit}
          style={{
            background:   'rgba(255,255,255,0.18)',
            color:        'white',
            border:       '1px solid rgba(255,255,255,0.35)',
            borderRadius: 6,
            padding:      '5px 12px',
            fontSize:     12,
            fontWeight:   600,
            cursor:       'pointer',
            display:      'inline-flex',
            alignItems:   'center',
            gap:          5,
          }}
        >
          <ArrowLeft size={13} /> Revenir à ma société
        </button>
      </div>
    </div>
  );
};
